import { AxiosError, AxiosHeaders } from 'axios';

import { REQUEST, TOKEN_TYPE_BEARER } from '@/constants/api';
import { LS_TOKEN_EXPIRES_AT_KEY, LS_TOKEN_KEY } from '@/constants/common';

import {
  ValidateExistingCustomerRequestParams,
  ValidateExistingCustomerResponse,
} from '@/types/api/auth';
import { Tenant } from '@/types/common';
import { RequiredDocumentSection } from '@/types/document';
import { OperationType, User } from '@/types/user';

import { buildRequestParams } from '@/utils/api';
import { saveLocal } from '@/utils/browser';
import { getOperationType } from '@/utils/operationType';
import { decodeTokenExpiryFromJwt } from '@/utils/token';

import { axios } from './axios';

const MILLISECONDS_IN_SECOND = 1000;

type VerifyOtpResponse = {
  token: string;
  access_token?: string;
  expires_in?: number;
};

export const getCurrentUser = async (includeRequiredDocuments = true) => {
  if (!axios.defaults.headers.common['Authorization']) {
    throw new AxiosError('Unauthenticated', '', undefined, undefined, {
      status: 401,
      data: {},
      statusText: 'Unauthenticated',
      config: {
        headers: new AxiosHeaders(),
      },
      headers: new AxiosHeaders(),
    });
  }
  const operationType = getOperationType();
  const result = await axios.get<User>(
    `${REQUEST.USER.INFO}${buildRequestParams({ type: operationType })}`,
  );

  const requiredSections = includeRequiredDocuments
    ? await axios.get<RequiredDocumentSection[]>(
        REQUEST.USER.REQUIRED_DOCUMENTS,
      )
    : { data: [] };

  return {
    time: Date.now(),
    user: {
      ...result.data,
    } as User,
    requiredSections: requiredSections.data,
  };
};

export const validateExistingCustomer = async ({
  token,
  tenant,
  jwe,
}: ValidateExistingCustomerRequestParams) => {
  const result = await axios.post<
    ValidateExistingCustomerRequestParams,
    ValidateExistingCustomerResponse
  >(
    REQUEST.AUTH.EXISTING_CUSTOMER,
    {
      token,
      ...(jwe && { type: 'jwe' }),
    },
    {
      headers: {
        'x-tenant': tenant,
      },
    },
  );

  axios.defaults.headers.common['x-tenant'] = result.data.tenant.toLowerCase();

  return result.data;
};

export const getOtp = async ({
  email,
  tenant,
  branchId,
}: {
  email: string;
  tenant: Tenant;
  branchId?: string;
}) => {
  const result = await axios.post(
    REQUEST.AUTH.GET_OTP,
    {
      email,
      branchId,
    },
    {
      headers: {
        'x-tenant': tenant,
      },
    },
  );

  return result.data;
};

const getExpiresIn = (token: string, expiresIn?: number) => {
  // Prefer expiry generated from token over response expiry
  let expiresInFromToken = decodeTokenExpiryFromJwt(token);
  if (typeof expiresInFromToken === 'string') {
    expiresInFromToken = Number(expiresInFromToken);
  }
  let expiresInFromResponse = expiresIn;
  if (expiresInFromResponse) {
    if (typeof expiresIn === 'string') {
      expiresInFromResponse = Number(expiresIn);
    }
    expiresInFromResponse =
      Date.now() + expiresInFromResponse * MILLISECONDS_IN_SECOND;
  }
  return expiresInFromToken || expiresInFromResponse;
};

export const verifyOtp = async ({
  payload,
  tenant,
}: {
  payload: {
    email: string;
    passcode: string;
    type?: OperationType;
  };
  tenant: Tenant;
}) => {
  const result = await axios.post<VerifyOtpResponse>(
    REQUEST.AUTH.VERIFY_OTP,
    payload,
    {
      headers: {
        'x-tenant': tenant,
      },
    },
  );

  const token = result.data.access_token || result.data.token;
  saveLocal(LS_TOKEN_KEY, token);
  let expiresIn = getExpiresIn(token, result.data.expires_in);
  if (expiresIn) {
    saveLocal(LS_TOKEN_EXPIRES_AT_KEY, expiresIn);
  }

  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

  return result.data;
};

// #region Refresh Token
type RefreshTokenResponse = {
  token: string;
  access_token: string;
  expires_in: number;
};

export async function refreshAccessTokenWithExpiredToken(
  expiredToken: string,
  tenant?: string,
) {
  const response = await axios.post<RefreshTokenResponse>(
    REQUEST.AUTH.REFRESH,
    {},
    {
      headers: {
        Authorization: `${TOKEN_TYPE_BEARER} ${expiredToken}`,
        ...(tenant && { 'x-tenant': tenant }),
      },
    },
  );

  const accessToken = response.data.access_token;
  axios.defaults.headers.common['Authorization'] =
    `${TOKEN_TYPE_BEARER} ${accessToken}`;
  saveLocal(LS_TOKEN_KEY, accessToken);

  const expiresIn = getExpiresIn(accessToken, response.data.expires_in);
  if (expiresIn) {
    saveLocal(LS_TOKEN_EXPIRES_AT_KEY, expiresIn);
  }

  return accessToken;
}

// #endregion Refresh Token
